import {
  ClientEvents,
  LastTrackerPosition,
  TrackerPosition,
  Vehicle,
} from "@/interfaces";
import api from "@/lib/axios";
import type { EngineDataLevel, RpmZones } from "@/lib/engine-data-level";
import { TagAndVehicleInfoResponse } from "./tags";
import { StopGroup } from "./trackReports";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

export interface VehicleDailyOverviewResponse {
  data: {
    daily_positions: TrackerPosition[];
    drivers_list: string[];
    events_data: {
      [key: string]: ClientEvents[];
    };
    first_movement: string;
    has_engine_on_info: boolean;
    engine_data_level?: EngineDataLevel;
    idle_is_estimated?: boolean;
    inside_safe_zone: boolean;
    last_position: LastTrackerPosition;
    stop_groups: StopGroup[];
    drivers_with_colors: Array<{
      name: string;
      color: string;
    }>;
    time_since_last_event: string;
    totals: {
      events_count: {
        over_speed: number;
      };
      average_speed: number;
      distance: number;
      fuel_expense: number;
      max_speed: number;
      timings: {
        movement: number;
        over_speed: number;
        stopped_with_ignition_on: number;
        engine_on: number;
        engine_idle?: number;
        /** Só para veículos de nível `can`, a partir da Fase 2B do backend. */
        rpm?: RpmZones;
        off: number;
      };
    };
    vehicle_plate: string;
    vehicle_title: string;
    vehicle_subtype: string;
    vehicle_type_name: string;
  };
}

export interface VehicleDistanceChartResponse {
  errors: boolean;
  data: number[];
}

interface DriverPermission {
  status: number;
  status_label: string;
  id: number;
  name: string;
}

export interface MaintenanceNotification {
  id: number;
  description?: string;
  date: string;
  odometer?: string;
  status?: string;
  type: string;
  expired?: boolean;
}

interface getVehiclesExpensesNotificationResponse {
  data: {
    notifications: MaintenanceNotification[];
    vehicle_title: string;
  };
  errors: boolean;
}
export interface MaintenanceNotificationGroup {
  date?: MaintenanceNotification[];
  odometer?: MaintenanceNotification[];
  both?: MaintenanceNotification[];
}
interface getVehiclesMaintenancesNotificationResponse {
  data: {
    last_odometer: number;
    notifications: MaintenanceNotificationGroup;
    vehicle_title: string;
  };
  errors: boolean;
}

export interface DailyTrackHistory {
  date: string;
  first_movement_date: string;
  last_movement_date: string;
  movement_time: number;
  distance: number;
  stopped_with_ignition_on_time: number;
  over_speed_count: number;
  over_speed_time: number;
  max_speed: number;
  extra_journey_distance: number;
}

export interface FuellingHistory {
  date: string;
  odometer: number;
  liters: number;
  price_per_liter: number;
  invoice_number: string;
  partial: number;
  driver_id: number;
  arla_liters: number;
  arla_price: number;
  amount: number;
}

export interface MaintenanceHistory {
  date: string;
  odometer: number;
  invoice_number: string;
  description: string;
  type: string | null;
  occurrence_date: string | null;
  driver_id: number;
  amount: number;
}

export interface RepairHistory {
  date: string;
  odometer: number;
  invoice_number: string;
  description: string;
  type: string | null;
  occurrence_date: string | null;
  driver_id: number;
  amount: number;
}

export interface VehicleExpenses {
  fuelling: {
    total_amount: number;
    history: FuellingHistory[];
  };
  maintenance: {
    total_amount: number;
    history: MaintenanceHistory[];
  };
  repairs: {
    total_amount: number;
    history: RepairHistory[];
  };
}

export interface VehicleData {
  vehicle_id: number;
  plate: string;
  title: string;
  vehicle_name: string;
  total_distance: number;
  total_time: number;
  daily_track_history: DailyTrackHistory[];
  expenses: VehicleExpenses;
  events: {
    harsh_braking: string;
    harsh_acceleration: string;
    overspeed: string;
  };
  fuel_consumption_avg: string;
  initial_odometer: number | null;
  final_odometer: number | null;
}

export interface FleetSummaryReportData {
  errors: boolean;
  data: VehicleData[];
}

export interface FleetSummaryReportDownloadData {
  errors: boolean;
  data: Blob;
}

export interface FleetSummaryTableProps {
  data: FleetSummaryReportData;
}

export const vehicleService = {
  async getVehiclesWithExpensesNotificationLevel({
    page,
    per_page,
    search,
    filters = {},
  }: DefaultGetRequestParams) {
    const response = await api.get<DefaultResponse<Vehicle>>(
      `/vehicle/load_expenses_notification_level`,
      {
        params: {
          page: page,
          per_page: per_page,
          search: search,
          ...filters,
        },
      },
    );
    return response.data;
  },

  async getVehicleData(vehicleId: number) {
    const response = await api.get<DefaultResponse<Vehicle, false>>(
      `/vehicle/${vehicleId}`,
    );
    return response.data;
  },

  async getVehicleDailyOverview(vehicleId: number) {
    const response = await api.get<VehicleDailyOverviewResponse>(
      `/vehicle/${vehicleId}/daily_overview`,
    );
    return response.data;
  },

  async getVehicleDistanceChart(vehicleId: number) {
    const response = await api.get<VehicleDistanceChartResponse>(
      `/vehicle/${vehicleId}/daily_overview/distance_chart`,
    );
    return response.data;
  },

  async blockVehicle(vehicleId: number, status: number) {
    const response = await api.post(`/block`, {
      vehicle_id: vehicleId,
      block_status: status ? 1 : 0,
    });
    return response.data;
  },

  async setVehicleAlarm(vehicleId: number, status: number) {
    const response = await api.put(`/vehicle/${vehicleId}/alarm`, {
      alarm_status: status ? 1 : 0,
    });
    return response.data;
  },

  async lockTrunk(vehicleId: number, action: "lock" | "unlock") {
    const response = await api.get(`/vehicle/${vehicleId}/lock_trunk`, {
      params: {
        action,
      },
    });
    return response.data;
  },

  async generateTempLink(vehicleId: number) {
    const response = await api.post(`/generate_link`, {
      vehicle_id: vehicleId,
    });
    return response.data;
  },

  async update(vehicleId: string | number, data: Partial<Vehicle>) {
    const response = await api.put(`/vehicle/${vehicleId}`, data);
    return response.data;
  },

  async show(vehicleId: number) {
    const response = await api.get<DefaultResponse<Vehicle, false>>(
      `/vehicle/${vehicleId}`,
    );
    return response.data;
  },

  async getDriversPermissions(vehicleId: number) {
    const response = await api.get<DefaultResponse<DriverPermission>>(
      `/vehicle/${vehicleId}/authorized_drivers`,
    );
    return response.data;
  },

  async getVehiclesExpensesNotification(vehicleId: number) {
    const response = await api.get<getVehiclesExpensesNotificationResponse>(
      `/vehicle/${vehicleId}/expenses_notification`,
    );
    return response.data;
  },

  async getVehiclesMaintenancesNotification(vehicleId: number) {
    const response = await api.get<getVehiclesMaintenancesNotificationResponse>(
      `/vehicle/${vehicleId}/maintenance_notification`,
    );
    return response.data;
  },

  async getMyFleetSummaryReport({
    start_date,
    end_date,
    vehicle_ids,
  }: {
    start_date: string;
    end_date: string;
    vehicle_ids: number[];
  }) {
    const response = await api.get<FleetSummaryReportData>(
      `/vehicle/my_fleet/summary_report`,
      {
        params: {
          start_date,
          end_date,
          vehicle_ids: vehicle_ids.join(","),
        },
      },
    );
    return response.data;
  },

  async getMyFleetSummaryReportExcel({
    start_date,
    end_date,
    vehicle_ids,
  }: {
    start_date: string;
    end_date: string;
    vehicle_ids: number[];
  }) {
    const response = await api.get(
      `/vehicle/my_fleet/summary_report/download`,
      {
        params: {
          start_date,
          end_date,
          vehicle_ids: vehicle_ids.join(","),
        },
        responseType: "blob",
      },
    );
    return response.data;
  },

  async loadVehiclesWithVideo() {
    const response =
      await api.get<TagAndVehicleInfoResponse>("vehicle/with_video");
    return response.data;
  },

  async loadVehiclesWithCanTelemetry() {
    const response = await api.get<TagAndVehicleInfoResponse>(
      "vehicle/with_can_telemetry",
    );
    return response.data;
  },
};
